import {
  ApolloClient,
  ApolloLink,
  NormalizedCacheObject,
  InMemoryCacheConfig,
  InMemoryCache,
  Observable
} from '@apollo/client';
import * as Sentry from '@sentry/react';
import { onError } from '@apollo/client/link/error';
import { createUploadLink } from 'apollo-upload-client';
import {
  isCloudflareChallengeError,
  isServerError,
  isServerParseError
} from './guards';
import { GraphQLError } from 'graphql';

export const REQUEST_ID_HEADER = Symbol('x-request-id');
export const CF_RAY_ID_HEADER = Symbol('CF-ray');

type CreateClientParams = {
  name: string;
  version?: string;
  cacheConfig?: InMemoryCacheConfig;
  connectToDevTools?: boolean;
  sentry?: {
    dsn: string;
    release: string;
  };
};

const DEFAULT_URL = '/graphql';

interface ErrorExtensions {
  requestId: string;
  cfRayId: string;
  [key: string]: any;
}

type ErrorWithExtensions<T> = Omit<T, 'extensions'> & {
  extensions: ErrorExtensions;
};

function addRequestExtensions<T extends GraphQLError>(
  error: T,
  response?: Response
): ErrorWithExtensions<T> {
  (error as Record<string, any>).extensions = {
    ...error.extensions,
    requestId: response?.headers?.get('x-request-id') || 'unknown',
    cfRayId: response?.headers?.get('CF-ray') || undefined
  } satisfies ErrorExtensions;
  return error as unknown as ErrorWithExtensions<T>;
}

export const doRequestHeadersLink = new ApolloLink((operation, forward) => {
  return new Observable((observer) => {
    const observable = forward(operation);
    const subscription = observable.subscribe({
      next(response) {
        try {
          const context = operation.getContext();
          if (response.errors) {
            response.errors = response.errors.map((error) =>
              addRequestExtensions(error, context.response)
            );
          }
          observer.next(response);
        } catch (err) {
          observer.error(err);
        }
      },
      error(networkError) {
        const context = operation.getContext();
        const newNetworkError = addRequestExtensions(
          networkError,
          context.response
        );
        observer.error(newNetworkError);
      },
      complete() {
        observer.complete();
      }
    });

    return () => subscription.unsubscribe();
  });
});

type CreateHandleErrorLinkParams = {
  sentry?: {
    dsn: string;
    release: string;
  };
};

export const createHandleErrorLink = (
  params: CreateHandleErrorLinkParams = {}
): ApolloLink =>
  onError(({ networkError, operation, forward }) => {
    function captureException(
      error: Error,
      options: { extra: Record<string, unknown> }
    ): void {
      Sentry.withScope((scope) => {
        for (const [key, value] of Object.entries(options.extra)) {
          scope.setExtra(key, value);
        }

        if (params.sentry) {
          scope.setContext('microfrontend', {
            dsn: params.sentry.dsn,
            release: params.sentry.release
          });
        }

        const context = operation.getContext();

        if (context.LOBSTER_ROLL_SENTRY_INDIRECT_STACK) {
          scope.setContext('LOBSTER_ROLL_SENTRY_INDIRECT_STACK', {
            stack: context.LOBSTER_ROLL_SENTRY_INDIRECT_STACK
          });
        }

        scope.captureException(error);
      });
    }

    // Handle Cloudflare Bot Management Captcha redirect
    if (isCloudflareChallengeError(networkError)) {
      const url = `/graphql/public/test`;
      const challenge = `challenge=${window.location.pathname}`;
      const s = window.location.search;
      const challengeUri = s.length
        ? `${url}${s}&${challenge}`
        : `${url}?${challenge}`;

      window.location.replace(challengeUri);
      return;
    }

    if (isServerParseError(networkError) || isServerError(networkError)) {
      const { response } = networkError;
      let url: Partial<URL>;
      try {
        url = new URL(response.url);
      } catch {
        url = { hostname: 'invalid-url.digitalocean.com' };
      }

      if (!url.hostname.endsWith('digitalocean.com')) {
        return;
      }

      if (networkError.statusCode === 403) {
        // Rbac Error so let sentry know
        captureException(networkError, {
          extra: {
            message: 'Permission Denied',
            name: networkError.name,
            statusCode: networkError.statusCode,
            operationName: operation.operationName,
            variables: operation.variables
          }
        });

        // a 403 that isn't cloudflare is likely rbac error
        networkError.name = 'PermissionDeniedError';
        networkError.message = 'Permission Denied';
      } else {
        captureException(networkError, {
          extra: {
            message: networkError.message,
            name: networkError.name,
            stack: networkError?.stack ?? '',
            cause: networkError.cause || 'No additional context provided',
            url,
            status: response.status
          }
        });

        return;
      }
    }

    // let the next link try and handle it
    return forward(operation);
  });

export const uploadLink = createUploadLink({
  uri: (operation) => {
    const url: string = operation.getContext().url || DEFAULT_URL;
    const search = new URLSearchParams(window.location.search);

    if (search.has('i')) {
      return `${url}?i=${search.get('i')}`;
    } else {
      return url;
    }
  },
  credentials: 'include'
});

const defaultConnectToDevTool = process.env.NODE_ENV !== 'production' || false;

export const createApolloClient = (
  params: CreateClientParams
): ApolloClient<NormalizedCacheObject> => {
  const {
    name,
    version,
    sentry,
    cacheConfig = {},
    connectToDevTools = defaultConnectToDevTool
  } = params;

  const cache = new InMemoryCache(cacheConfig);

  return new ApolloClient({
    link: ApolloLink.from([
      createHandleErrorLink({ sentry }),
      doRequestHeadersLink,
      uploadLink
    ]),
    cache,
    assumeImmutableResults: true,
    connectToDevTools,
    name,
    version
  });
};
